import { useNamespacedTranslation } from '../../../hooks/useNamespacedTranslation';
import { useCurrentLocale } from '../../../context/I18n';
import { useObjectLabelsQuery } from '../__generated__/graphql';

export type ObjectLabels = {
  employee: string;
  employeePlural: string;
  location: string;
  locationPlural: string;
  department: string;
  departmentPlural: string;
};

// The managed package's default (English) object labels. Salesforce only ships
// object translations for English, German and French, so for any other Hub
// language a non-renamed object comes back from the UI API in English. We must
// not let that English default override the Hub's own localized translation —
// otherwise e.g. a Russian user would see "Department" instead of "Отдел".
const PACKAGE_DEFAULT_LABELS: ObjectLabels = {
  employee: 'Employee',
  employeePlural: 'Employees',
  location: 'Location',
  locationPlural: 'Locations',
  department: 'Department',
  departmentPlural: 'Departments',
};

// Exposes the org's object labels so the Hub reflects custom terminology when an
// admin renamed Employee/Location/Department in Salesforce. The Salesforce label
// is only used when it is a genuine override (differs from the package default);
// otherwise we keep the localized `objectLabels` translation. Values are
// returned directly (with the translation as fallback) so callers don't repeat
// the logic.
export const useObjectLabels = (): ObjectLabels => {
  const t = useNamespacedTranslation('objectLabels');
  // Pass the employee's active language so Salesforce returns the labels in that
  // language, matching how other label queries (e.g. `data(locale)`) work.
  const locale = useCurrentLocale();
  const labels = useObjectLabelsQuery({
    variables: { locale },
    fetchPolicy: 'cache-first',
  }).data?.objectLabels;

  const resolve = (
    salesforceLabel: string | undefined,
    key: keyof ObjectLabels,
  ): string =>
    salesforceLabel && salesforceLabel !== PACKAGE_DEFAULT_LABELS[key]
      ? salesforceLabel
      : t(key);

  return {
    employee: resolve(labels?.employee.label, 'employee'),
    employeePlural: resolve(labels?.employee.labelPlural, 'employeePlural'),
    location: resolve(labels?.location.label, 'location'),
    locationPlural: resolve(labels?.location.labelPlural, 'locationPlural'),
    department: resolve(labels?.department.label, 'department'),
    departmentPlural: resolve(
      labels?.department.labelPlural,
      'departmentPlural',
    ),
  };
};
